import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../../../../models/chart_model";
import loader from "../../../../../../components/recharts/loader.gif";
import {
  getTopBuyersSellers,
  getTopBuyerSellerSlotwise,
  TopBuyerSellerResponse
} from "../../../../../../Rest_api/RestApi";
import ToggleTabs from "../../../utility/ChartTableToggle";
import BuySellDelta from "./BuySellDelta";
import { SearchBox } from "../../../../../../components/Search";

const DataTabs = ["Buy/Sell", "Buy/Sell Delta"];

const VIEW_OPTIONS = [
  { id: "buyers", label: "Buyers" },
  { id: "sellers", label: "Sellers" },
];

const DELTA_VIEW_OPTIONS = [
  { id: "drawal", label: "Drawal" },
  { id: "injection", label: "Injection" },
];

function ViewSelector({
  selected,
  onSelectionChange,
  options,
}: {
  selected: string;
  onSelectionChange: (selected: string) => void;
  options: { id: string; label: string }[];
}) {
  return (
    <div className="flex items-center gap-4">
      {options.map((option) => {
        const isSelected = selected === option.id;
        return (
          <button
            key={option.id}
            onClick={() => onSelectionChange(option.id)}
            className="flex items-center gap-2 cursor-pointer focus:outline-none px-2 py-1"
          >
            <div
              className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? "border-transparent" : "border-gray-400"
                }`}
              style={{ backgroundColor: isSelected ? "#34656D" : "transparent" }}
            >
              {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
            </div>
            <span
              className={`font-medium text-xs transition-colors ${isSelected ? "text-gray-800" : "text-gray-500"
                }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function BuySellAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedExchanges = [],
  selectedMarkets = [],
  activeChartTab,
  setActiveChartTab,
  setSharedData,
  setSharedView,
  setSharedDeltaData,
  setSharedDeltaView,
}: {
  selectedBlock?: any[];
  refreshKey?: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
  activeChartTab?: any;
  setActiveChartTab?: any;
  setSharedData?: (data: any) => void;
  setSharedView?: (view: "buyers" | "sellers") => void;
  setSharedDeltaData?: (data: any) => void;
  setSharedDeltaView?: (view: "drawal" | "injection") => void;
}) {
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TopBuyerSellerResponse | null>(null);
  const [view, setView] = useState<"buyers" | "sellers">("buyers");

  // Delta View State
  const [deltaView, setDeltaView] = useState<"drawal" | "injection">("drawal");

  const [internalActiveTab, setInternalActiveTab] = useState<string>(DataTabs[0]);
  const activeTab = activeChartTab || internalActiveTab;
  const setActiveTab = setActiveChartTab || setInternalActiveTab;

  // New state for local date filtering
  const [selectedFilterDate, setSelectedFilterDate] = useState<any[]>([]);
  const [deltaSelectedDates, setDeltaSelectedDates] = useState<any[]>([]); // New state for Delta
  const [availableDates, setAvailableDates] = useState<any[]>([]);

  // Sync available dates from Redux and set default selection
  useEffect(() => {
    const dateKey = "Power Market";
    const trendDates = DefaultDates?.[dateKey]?.defaultTrendDates || [];

    setAvailableDates(trendDates);

    if (trendDates.length === 0) {
      if (selectedFilterDate.length > 0) {
        setSelectedFilterDate([]);
      }
      if (deltaSelectedDates.length > 0) {
        setDeltaSelectedDates([]);
      }
      return;
    }

    const primaryDate = trendDates[0];
    const currentSelection = selectedFilterDate[0];

    if (!currentSelection || (primaryDate && primaryDate.value !== currentSelection.value)) {
      if (primaryDate) {
        setSelectedFilterDate([primaryDate]);
      } else {
        setSelectedFilterDate([]);
      }
    }

    if (trendDates.length >= 2) {
      setDeltaSelectedDates([trendDates[0], trendDates[1]]);
    } else if (trendDates.length > 0) {
      setDeltaSelectedDates(trendDates);
    }
  }, [DefaultDates, setAvailableDates]);

  useEffect(() => {
    if (activeTab !== "Buy/Sell") return;

    if (selectedFilterDate.length === 0) {
      setData(null);
      setSharedData?.(null);
      return;
    }

    const { value } = selectedFilterDate[0];
    const [startDate, endDate] = value.split(",");

    const fetchData = async () => {
      try {
        setLoading(true);
        const exch = selectedExchanges;
        const prod = selectedMarkets;

        let response;
        if (selectedBlock && selectedBlock.length > 0) {
          // If blocks are selected, use the slot-wise API
          response = await getTopBuyerSellerSlotwise(
            startDate,
            endDate || startDate,
            exch,
            prod,
            selectedBlock
          );
        } else {
          // Otherwise use the standard API
          response = await getTopBuyersSellers(
            startDate,
            endDate || startDate,
            exch,
            prod
          );
        }

        setData(response);
        setSharedData?.(response);
      } catch (err) {
        console.error("Error fetching top buyers/sellers", err);
        setError?.("Failed to fetch top buyers/sellers data");
        setData(null);
        setSharedData?.(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedFilterDate, refreshKey, (selectedExchanges || []).join(","), (selectedMarkets || []).join(","), (selectedBlock || []).join(","), setError, activeTab, setSharedData]);

  const handleTxViewChange = (newView: string) => { // Type as string to match ViewSelector
    const v = newView as "buyers" | "sellers";
    setView(v);
    setSharedView?.(v);
  };

  const handleDeltaViewChange = (newView: string) => {
    const v = newView as "drawal" | "injection";
    setDeltaView(v);
    setSharedDeltaView?.(v);
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    const source = view === "buyers" ? data.top_buyers : data.top_sellers;
    return source.map(item => ({
      ...item,
      name: item.name.length > 12 ? item.name.substring(0, 12) + "..." : item.name
    }));
  }, [data, view]);

  const legends = useMemo(() => {
    const option = VIEW_OPTIONS.find(o => o.id === view);
    return [{
      dataKey: "value",
      name: option?.label || "Value",
      type: ChartType.Bar,
      unit: "MWh",
    }];
  }, [view]);

  // Determine Title based on active tab and view
  const chartTitle = useMemo(() => {
    const blockText = selectedBlock && selectedBlock.length > 0
      ? ` (${selectedBlock.join(",")} Time Block)`
      : "";

    if (activeTab === "Buy/Sell") {
      const baseText = `Top 10 ${view === "buyers" ? "Buyers" : "Sellers"}`;
      return (
        <>
          {baseText}
          {blockText && <span className="text-[#F1935C]"> in{blockText}</span>}
        </>
      );
    } else {
      const baseText = `Top ${deltaView === "drawal" ? "Drawal" : "Injection"} Changes in`;
      return (
        <>
          Top 10 States with Largest {deltaView === "drawal" ? "Drawal" : "Injection"} Changes
          {blockText && <span className="text-[#F1935C]"> in{blockText}</span>}
        </>
      );
    }
  }, [activeTab, view, deltaView, selectedBlock]);

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white w-full h-full rounded-2xl p-2 flex flex-col !min-h-[300px]">
      <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] bg-[#FFF] pb-1 flex-shrink-0 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="chartHeading !text-[#34656D] !text-sm">
            {chartTitle}
          </h2>
        </div>
        <div className="flex gap-x-5 justify-end flex-shrink-0 items-center">
          {activeTab === "Buy/Sell" && (
            <>
              <SearchBox
                options={availableDates}
                isMany={false}
                placeholder="Select Date"
                selectedOptions={selectedFilterDate}
                onChange={(val: any) => {
                  if (val) {
                    setSelectedFilterDate(Array.isArray(val) ? val : [val]);
                  } else {
                    setSelectedFilterDate([]);
                  }
                }}
              />
              <ViewSelector
                selected={view}
                onSelectionChange={handleTxViewChange}
                options={VIEW_OPTIONS}
              />
            </>
          )}
          {activeTab === "Buy/Sell Delta" && (
            <>
              <div className="min-w-[250px]">
                <SearchBox
                  options={availableDates}
                  isMany={true}
                  placeholder="Select Dates"
                  selectedOptions={deltaSelectedDates}
                  onChange={(val: any) => {
                    if (val && val.length > 0) {
                      setDeltaSelectedDates(Array.isArray(val) ? val : [val]);
                    } else {
                      setDeltaSelectedDates([]);
                    }
                  }}
                />
              </div>
              <ViewSelector
                selected={deltaView}
                onSelectionChange={handleDeltaViewChange}
                options={DELTA_VIEW_OPTIONS}
              />
            </>
          )}
          <ToggleTabs
            tabs={DataTabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>

      {activeTab === "Buy/Sell" ? (
        loading ? (
          <div className="w-full bg-white flex items-center justify-center h-full flex-1">
            <img src={loader} width={200} alt="loader" className="block" />
          </div>
        ) : !data || chartData.length === 0 ? (
          <div className="w-full bg-white flex items-center justify-center h-full flex-1">
            <p className="text-gray-500">No data available</p>
          </div>
        ) : (
          <ReMixChart
            chartHeight="calc(100% - 50px)"
            data={chartData}
            legends={legends}
            xDataKey="name"
            isTimeSlot={false}
            yAxisLabel="MWh"
            showBrush={false}
            showToolbox={false}
            top={10}
            bottom={30}
            barCategoryGap={0}
            xAxisInterval={0}
            fontSize={10}
            colorByPoint={true}
            barColors={[
              "#FF6565", // Red
              "#2196f3", // Blue
              "#5BE854", // Green
              "#EE65FF", // Purple
              "#ff9f43", // Orange
              "#EEEE59", // Yellow
              "#ffbe76", // Peach
              "#FF6FBA", // Pink
              "#C9C6C6", // Light Grey
              "#817676", // Dark Grey
            ]}
          />
        )
      ) : (
        // Render Buy/Sell Delta chart with new props
        <BuySellDelta
          selectedBlock={selectedBlock}
          refreshKey={refreshKey || 0}
          setError={setError}
          // setUniqueTargetDate, // Unused
          selectedExchanges={selectedExchanges}
          selectedMarkets={selectedMarkets}
          view={deltaView}
          setSharedData={setSharedDeltaData}
          comparisonDates={deltaSelectedDates}
        />
      )}
    </div>
  );
}